import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {convertIssueToDiscussionMutation as convertIssueToDiscussionMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  ConvertIssueToDiscussionInput as ServerConvertIssueToDiscussionInput,
  ConvertIssueToDiscussionMutationMutation,
} from '@github-ui/server-persisted-queries/types/convertIssueToDiscussionMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  ConvertIssueToDiscussionInput,
  convertIssueToDiscussionMutation,
  convertIssueToDiscussionMutation$data,
} from './__generated__/convertIssueToDiscussionMutation.graphql'
import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'

export type ConvertIssueToDiscussionRouteInput = Omit<
  ServerConvertIssueToDiscussionInput,
  'issueId' | 'clientMutationId'
>
// RELAY-CLEANUP(convertIssueToDiscussionMutation): Drop the Relay response type when the fallback is removed.
export type ConvertIssueToDiscussionMutationResponse =
  | convertIssueToDiscussionMutation$data
  | ConvertIssueToDiscussionMutationMutation

type IssueMutationProps = {
  environment: Environment
  input: ConvertIssueToDiscussionInput
  onError?: (error: Error) => void
  onCompleted?: (response: convertIssueToDiscussionMutation$data) => void
}

export function commitConvertIssueToDiscussionMutation({
  environment,
  input: {issueId, categoryId},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: ConvertIssueToDiscussionInput = {
    issueId,
    categoryId,
  }

  return commitMutation<convertIssueToDiscussionMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation convertIssueToDiscussionMutation($input: ConvertIssueToDiscussionInput!) @raw_response_type {
        convertIssueToDiscussion(input: $input) {
          discussion {
            url
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}

export function useConvertIssueToDiscussionMutation({
  owner,
  repository,
  number,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  onCompleted?: (response: ConvertIssueToDiscussionMutationMutation) => void
  onError?: (error: Error) => void
}): UseMutationResult<ConvertIssueToDiscussionMutationMutation, Error, {input: ConvertIssueToDiscussionRouteInput}> {
  return useMutation({
    mutationFn: ({input}) => submitConvertIssueToDiscussionMutation({owner, repository, number, input}),
    onSuccess: onCompleted,
    onError,
  })
}

export function submitConvertIssueToDiscussionMutation({
  owner,
  repository,
  number,
  input,
}: {
  owner: string
  repository: string
  number: number
  input: ConvertIssueToDiscussionRouteInput
}) {
  return submitIssueLifecycleMutation<ConvertIssueToDiscussionMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}/discussion.json`,
    method: 'POST',
    queryId: convertIssueToDiscussionMutationQueryId,
    input,
    errorMessage: 'Issue conversion mutation failed',
    isSuccess: data => data.convertIssueToDiscussion?.discussion != null,
    payloadErrors: data => data.convertIssueToDiscussion?.errors,
  })
}
